import { Outlet, Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Menu, X, ArrowRight, ChevronDown } from "lucide-react";
import ChatBot from "../ChatBot";
import { LOGO_URL, megaMenuData, navLinks } from "@/data/navigationData";
import { TopBar } from "./TopBar";
import { MegaMenuPanel } from "./MegaMenuPanel";
import { MobileMenu } from "./MobileMenu";
import { Footer } from "./Footer";
import CookieBanner from "../CookieBanner";

export default function PublicLayout() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openMega, setOpenMega] = useState(null);
  const megaTimeoutRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setOpenMega(null);
  }, [location]);

  const handleMegaEnter = (label) => {
    clearTimeout(megaTimeoutRef.current);
    setOpenMega(label);
  };

  const handleMegaLeave = () => {
    megaTimeoutRef.current = setTimeout(() => setOpenMega(null), 200);
  };

  return (
    <div className="min-h-screen">
      <TopBar isScrolled={isScrolled} />

      {/* ========== MAIN NAVBAR ========== */}
      <header
        className={`sticky top-0 z-50 transition-all duration-500 bg-[#1A120B] ${
          isScrolled ? "shadow-[0_6px_40px_rgba(0,0,0,0.5)] border-b border-[#C9922A]/15" : ""
        }`}
        data-testid="main-navbar"
      >
        <div className="h-px bg-gradient-to-r from-transparent via-[#C9922A]/40 to-transparent" />

        <div className="relative">
          <div className="container-hh">
            <div className={`flex items-center justify-between transition-all duration-300 ${isScrolled ? "h-[68px]" : "h-[78px]"}`}>

              {/* Logo */}
              <Link to="/" className="flex items-center flex-shrink-0 group" data-testid="nav-logo">
                <div className={`relative rounded-xl overflow-hidden border border-[#C9922A]/20 transition-all duration-300 group-hover:border-[#C9922A]/40 ${isScrolled ? "p-1" : "p-1.5"}`}>
                  <img
                    src={LOGO_URL}
                    alt="Happyhouse conciergerie"
                    className={`w-auto rounded-lg transition-all duration-300 ${isScrolled ? "h-9" : "h-11"}`}
                  />
                </div>
              </Link>

              {/* Navigation links — desktop */}
              <nav className="hidden lg:flex items-center gap-0.5">
                {navLinks.map((link) => {
                  const isActive = location.pathname === link.path ||
                    (link.hasMega && megaMenuData[link.label]?.items.some(c => location.pathname === c.path));

                  if (link.hasMega) {
                    return (
                      <div
                        key={link.label}
                        onMouseEnter={() => handleMegaEnter(link.label)}
                        onMouseLeave={handleMegaLeave}
                      >
                        <Link
                          to={link.path}
                          className="relative flex items-center gap-1.5 px-4 py-2.5 group"
                          data-testid={`nav-link-${link.label.toLowerCase()}`}
                        >
                          <span className={`text-[12px] font-semibold uppercase tracking-[0.1em] transition-colors duration-300 ${
                            isActive ? "text-[#C9922A]" : "text-white/70 group-hover:text-white"
                          }`}>
                            {link.label}
                          </span>
                          <ChevronDown className={`w-3 h-3 transition-all duration-300 ${
                            openMega === link.label ? "rotate-180 text-[#C9922A]" : isActive ? "text-[#C9922A]/60" : "text-white/40 group-hover:text-white/60"
                          }`} />
                          <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] rounded-full bg-gradient-to-r from-[#C9922A] to-[#E8C874] transition-all duration-400 ease-out ${
                            isActive || openMega === link.label ? "w-6" : "w-0 group-hover:w-6"
                          }`} />
                        </Link>
                      </div>
                    );
                  }

                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="relative px-4 py-2.5 group"
                      data-testid={`nav-link-${link.label.toLowerCase()}`}
                    >
                      <span className={`text-[12px] font-semibold uppercase tracking-[0.1em] transition-colors duration-300 ${
                        isActive ? "text-[#C9922A]" : "text-white/70 group-hover:text-white"
                      }`}>
                        {link.label}
                      </span>
                      <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-[2px] rounded-full bg-gradient-to-r from-[#C9922A] to-[#E8C874] transition-all duration-400 ease-out ${isActive ? "w-6" : "w-0 group-hover:w-6"}`} />
                    </Link>
                  );
                })}
              </nav>

              {/* CTA Button — desktop */}
              <div className="hidden lg:flex items-center">
                <Link to="/proprietaires#contact-form" className="relative group overflow-hidden" data-testid="cta-confier-bien">
                  <span className="relative z-10 inline-flex items-center gap-2 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#1A120B] bg-gradient-to-r from-[#C9922A] to-[#E8C874] rounded-full transition-all duration-300 group-hover:shadow-[0_4px_24px_rgba(201,146,42,0.4)]">
                    Confier mon bien
                    <ArrowRight className="w-3 h-3 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden p-2.5 rounded-xl border border-white/10 hover:bg-white/5 hover:border-[#C9922A]/30 transition-all duration-300"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Menu"
                data-testid="mobile-menu-btn"
              >
                {isMenuOpen ? <X className="w-5 h-5 text-white/70" /> : <Menu className="w-5 h-5 text-white/70" />}
              </button>
            </div>
          </div>

          {/* ========== MEGA MENU PANELS (desktop) ========== */}
          {navLinks.filter(l => l.hasMega).map((link) => (
            <div
              key={link.label}
              onMouseEnter={() => handleMegaEnter(link.label)}
              onMouseLeave={handleMegaLeave}
            >
              <MegaMenuPanel
                data={megaMenuData[link.label]}
                isOpen={openMega === link.label}
              />
            </div>
          ))}
        </div>

        <div className={`h-[2px] transition-opacity duration-500 ${isScrolled ? "opacity-100" : "opacity-40"}`}>
          <div className="h-full bg-gradient-to-r from-[#C9922A]/40 via-[#C9922A]/60 to-[#C9922A]/40" />
        </div>

        <MobileMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} currentPath={location.pathname} />
      </header>

      {/* ========== PAGE CONTENT ========== */}
      <main>
        <Outlet />
      </main>

      <Footer />
      <ChatBot />
      <CookieBanner />
    </div>
  );
}
